import * as React from 'react';
import { observer } from 'mobx-react';
import { ICycleConfiguration } from 'typings/cycleConfiguration';
import { FormGroup } from '@payscale/design';
import { parseDate, formatDate, dateValidForCycle } from 'utils/dateUtils';
import { configurationsStore } from 'utils/MobxDataManager/singletons';
import './createCycleConfigDialog.scss';
import { CalendarTileProperties } from 'react-calendar';
import { CustomDatePicker } from 'nomst/Components/Utils/customDatePicker';

export interface ICreateCycleConfigDialogProps {
    newCycleConfiguration: ICycleConfiguration;
    onCancel: () => void;
    onClose: () => void;
    onConfirm: (newCycleConfiguration: ICycleConfiguration) => void;
}

interface ICreateCycleConfigDialogState {
    endDate: string;
    payEffectiveDate: string;
}

export class CreateCycleConfigDialog
    extends React.Component<ICreateCycleConfigDialogProps, ICreateCycleConfigDialogState> {
    constructor(props: ICreateCycleConfigDialogProps) {
        super(props);
        this.handlePayEffectiveDateChange = this.handlePayEffectiveDateChange.bind(this);
        this.handleEndDateChange = this.handleEndDateChange.bind(this);
        this.handleClickClose = this.handleClickClose.bind(this);
        this.handleClickCancel = this.handleClickCancel.bind(this);
        this.handleClickConfirm = this.handleClickConfirm.bind(this);
        this.state = Object.assign({}, this.state, {
            endDate: '',
            payEffectiveDate: ''
        });
    }

    render(): React.ReactNode {
        return (
            <div className="pxl-modal">
                <div className="nomst-create-cycle-dialog-content">
                    <button
                        className="close"
                        onClick={this.handleClickClose}
                    >
                        <i className="icon-cancel" />
                    </button>
                    <div className="header-section">
                        <h2>
                            Create a New Cycle
                        </h2>
                    </div>
                    {this.renderCreateCycleConfigurationForm()}
                    <div className="action-section">
                        <button className="pxl-link" onClick={this.handleClickCancel}>Cancel</button>
                        <button
                            onClick={this.handleClickConfirm}
                            disabled={!this.isReadyToSave()}
                            className="pxl-btn pxl-btn--primary create-cycle-button"
                        >
                            Create Cycle
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    private disableDateTiles(tileProps: CalendarTileProperties): boolean {
        const currentEffectiveDays = configurationsStore.allCreatedCyclePayEffectiveDates;
        const tileYear = tileProps.date.getFullYear();
        const tileMonth = tileProps.date.getMonth();
        const tileDay = tileProps.date.getDate();

        return (tileProps.view === 'month') &&
            currentEffectiveDays.some(disabledDate =>
                tileYear === disabledDate.getFullYear() &&
                tileMonth === disabledDate.getMonth() &&
                tileDay === disabledDate.getDate()
            );
    }

    private renderCreateCycleConfigurationForm(): React.ReactNode {
        const { newCycleConfiguration } = this.props;
        const classNamePayEffectiveDate = newCycleConfiguration.payEffectiveDate ? '' : 'create-cycle-form__date--required';
        const classNameApprovalDate = newCycleConfiguration.endDate ? '' : 'create-cycle-form__date--required';

        return (
            <div className="create-cycle-form">
                <p className="form-description">
                    Team cycles require a pay effective date and a date to close the cycle.
                    Enter your dates below.
                </p>
                <fieldset>
                    <div className="create-cycle-form__section">
                        <FormGroup
                            className="create-cycle-form__pay-effective-date"
                            error={this.getRequiredText(this.state.payEffectiveDate)}
                        >
                            <label htmlFor="payEffectiveDate">
                                Pay Effective Date
                            </label>
                            <CustomDatePicker
                                id="payEffectiveDate"
                                value={parseDate(this.state.payEffectiveDate)}
                                onChange={this.handlePayEffectiveDateChange}
                                className={`create-cycle-form__date ${classNamePayEffectiveDate}`}
                                tileDisabled={this.disableDateTiles}
                                clearIcon={null}
                            />
                        </FormGroup>
                        <FormGroup
                            className="create-cycle-form__final-date"
                            error={this.getRequiredText(this.state.endDate)}
                        >
                            <label htmlFor="endDate">
                                Final Approval Date
                            </label>
                            <CustomDatePicker
                                id="endDate"
                                value={parseDate(this.state.endDate)}
                                onChange={this.handleEndDateChange}
                                className={`create-cycle-form__date ${classNameApprovalDate}`}
                                clearIcon={null}
                            />
                        </FormGroup>
                    </div>
                </fieldset>
            </div>
        );
    }

    private isReadyToSave(): boolean {
        return !!this.state.endDate && !!this.state.payEffectiveDate;
    }

    private getRequiredText(dateText: string): string {
        return !dateText ? 'Required' : '';
    }

    private handlePayEffectiveDateChange(value: Date): void {
        if(value != null && dateValidForCycle(value, 10)) {
            this.setState({payEffectiveDate: formatDate(value).toString()});
        } else {
            this.setState({payEffectiveDate: '' });
        }
    }

    private handleEndDateChange(value:  Date): void {
        if(value != null && dateValidForCycle(value, 10)) {
            this.setState({endDate: formatDate(value).toString()});
        } else {
            this.setState({endDate: '' });
        }
    }

    private handleClickClose(): void {
        this.props.onClose();
    }

    private handleClickCancel(): void {
        this.props.onCancel();
    }

    private handleClickConfirm(): void {
        this.props.newCycleConfiguration.endDate = this.state.endDate;
        this.props.newCycleConfiguration.payEffectiveDate = this.state.payEffectiveDate;
        this.props.onConfirm(this.props.newCycleConfiguration);
        this.props.onClose();
    }
}

export default observer(CreateCycleConfigDialog);